import { PaginatedDataDTO, PaginationDTO } from '@bottega52/commons-pagination';
import { TextField, WithStyles } from '@material-ui/core';
import { createStyles, withStyles } from '@material-ui/core/styles';
import { SearchOutlined } from '@material-ui/icons';
import { Stack } from '@mui/material';
import { DataGrid, GridColumns, GridRenderCellParams } from '@mui/x-data-grid';
import _ from 'lodash';
import React from 'react';
import { IMechanicalCylinderKeyRelationInDTO } from '../../repository/jago/model/input/IMechanicalCylinderKeyRelationInDTO';
import translations from '../../translations/i18next';
import ReduxLanguage from '../ReduxLanguage';

const styles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 0,
  },
  searchContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 10,
    marginBottom: 10,
    padding: 10,
    backgroundColor: 'white',
    borderRadius: 4,
  },
  tableContainer: {
    display: 'flex',
    backgroundColor: 'white',
    height: 400,
    width: '100%'
  }
});

export interface IAssociatedCylindersTableProps extends WithStyles<typeof styles> {
  relations: PaginatedDataDTO<IMechanicalCylinderKeyRelationInDTO>;
  onFetchElementsOnPage: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSearch: (cylinderType: string, doorName: string) => void;
  onRowClick?: (cylinderData: any) => void;
  rowsPerPage: number;
  isLoading?: boolean;
  useClientSideFiltering?: boolean;
}

export interface IAssociatedCylindersTableState {
  cylinderTypeFilter: string;
  doorNameFilter: string;
}

class AssociatedCylindersTable extends React.Component<IAssociatedCylindersTableProps, IAssociatedCylindersTableState> {
  private cylindersColumns: GridColumns;
  private debouncedSearch = _.debounce(() => {
    const { cylinderTypeFilter, doorNameFilter } = this.state;
    this.props.onSearch(cylinderTypeFilter, doorNameFilter);
  }, 500);

  constructor(props: IAssociatedCylindersTableProps) {
    super(props);
    this.state = {
      cylinderTypeFilter: '',
      doorNameFilter: '',
    };

    this.cylindersColumns = [
      {
        field: 'doorName',
        headerName: translations.t('mks.doorName'),
        type: 'string',
        sortable: false,
        filterable: false,
        minWidth: 200,
        flex: 2,
        renderCell: (params: GridRenderCellParams<any>) => {
          const relation = params.row;
          return <h3 style={{ marginLeft: 0, fontWeight: 'bold' }}>{relation.cylinder.doorName || 'N/A'}</h3>
        }
      },
      {
        field: 'type',
        headerName: translations.t('mks.type'),
        type: 'string',
        sortable: false,
        filterable: false,
        minWidth: 120,
        flex: 1,
        renderCell: (params: GridRenderCellParams<any>) => {
          return params.row.cylinder.type || 'N/A';
        }
      },
      {
        field: 'marking',
        headerName: translations.t('mks.marking'),
        type: 'string',
        sortable: false,
        filterable: false,
        minWidth: 120,
        flex: 1,
        renderCell: (params: GridRenderCellParams<any>) => {
          return params.row.cylinder.marking || 'N/A';
        }
      },
      {
        field: 'insideLength',
        headerName: translations.t('mks.insideLength'),
        type: 'string',
        sortable: false,
        filterable: false,
        minWidth: 100,
        flex: 1,
        renderCell: (params: GridRenderCellParams<any>) => {
          return params.row.cylinder.insideLength || 'N/A';
        }
      },
      {
        field: 'outsideLength',
        headerName: translations.t('mks.outsideLength'),
        type: 'string',
        sortable: false,
        filterable: false,
        minWidth: 100,
        flex: 1,
        renderCell: (params: GridRenderCellParams<any>) => {
          return params.row.cylinder.outsideLength || 'N/A';
        }
      },
      {
        field: 'quantityKeys',
        headerName: translations.t('mks.quantityKeys'),
        type: 'number',
        sortable: false,
        filterable: false,
        minWidth: 100,
        flex: 1,
        renderCell: (params: GridRenderCellParams<any>) => {
          return params.row.cylinder.quantityKeys || 0;
        }
      },
    ];
  }

  onFilterChange(field: 'cylinderTypeFilter' | 'doorNameFilter', value: string) {
    this.setState({ [field]: value } as any, () => {
      if (this.props.useClientSideFiltering) {
        this.props.onSearch(this.state.cylinderTypeFilter, this.state.doorNameFilter);
      } 
      else {
        this.debouncedSearch();
      }
    });
  }

  public render() {
    const { classes, relations, rowsPerPage, isLoading, onFetchElementsOnPage, onPageSizeChange, onRowClick, useClientSideFiltering } = this.props;
    const { cylinderTypeFilter, doorNameFilter } = this.state;

    const hasRelations = relations && relations.content && relations.content.length > 0;

    return (
      <div className={classes.container}>
        <div className={classes.searchContainer}>
          <TextField
            label={<ReduxLanguage languageKey="mks.filterByDoorName" />}
            variant="outlined"
            size="small"
            value={doorNameFilter}
            onChange={(e) => this.onFilterChange('doorNameFilter', e.target.value)}
            style={{ minWidth: 200 }}
          />
          <TextField
            label={<ReduxLanguage languageKey="mks.filterByType" />}
            variant="outlined"
            size="small"
            value={cylinderTypeFilter}
            onChange={(e) => this.onFilterChange('cylinderTypeFilter', e.target.value)}
            style={{ minWidth: 200 }}
          />
        </div>

        <div className={classes.tableContainer}>
          <DataGrid
            getRowId={(row) => row.id}
            rows={relations.content || []}
            columns={this.cylindersColumns}
            rowHeight={50}
            loading={isLoading}
            onPageSizeChange={onPageSizeChange}
            onPageChange={onFetchElementsOnPage}
            onRowClick={onRowClick ? (params) => onRowClick(params.row.cylinder) : undefined}
            page={(relations?.pagination as PaginationDTO)?.number}
            rowCount={(relations?.pagination as PaginationDTO)?.totalElements}
            pageSize={rowsPerPage}
            paginationMode="server"
            disableColumnMenu
            disableColumnSelector
            componentsProps={{
              pagination: {
                labelRowsPerPage: translations.t('forms.rowsPerPage')
              }
            }}
            components={{
              NoRowsOverlay: () => (
                <Stack height="100%" alignItems="center" justifyContent="center">
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                    <SearchOutlined style={{ alignSelf: 'center' }} />
                    <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}>
                      <ReduxLanguage languageKey='mks.noAssociatedCylinders' />
                    </h3>
                  </div>
                </Stack>
              ),
              NoResultsOverlay: () => (
                <Stack height="100%" alignItems="center" justifyContent="center">
                  <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}>
                    <ReduxLanguage languageKey='mks.noAssociatedCylinders' />
                  </h3>
                </Stack>
              )
            }}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
              },
            }}
          />
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(AssociatedCylindersTable);
